import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import ChatPanel from '../components/ChatPanel';
import { Button } from '../components/ui/button';
import { Loader2, Plus, Users } from 'lucide-react';
import { listHires } from '../chatApi';
import { getWorkerById } from '../mock';
import { Toaster } from '../components/ui/toaster';

const Workspace = () => {
  const navigate = useNavigate();
  const [hires, setHires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await listHires();
        // Deduplicate by worker_id, keep latest first
        const seen = new Set();
        const unique = [];
        for (const h of data) {
          if (!seen.has(h.worker_id)) {
            seen.add(h.worker_id);
            unique.push(h);
          }
        }
        setHires(unique);
        if (unique.length && !activeId) setActiveId(unique[0].worker_id);
      } finally {
        setLoading(false);
      }
    })();
     
  }, []);

  const activeWorker = useMemo(() => {
    if (!activeId) return null;
    const w = getWorkerById(activeId);
    if (w) return w;
    // Custom worker fallback (not in roster) — build minimal shape from hire record
    const h = hires.find((x) => x.worker_id === activeId);
    if (!h) return null;
    return {
      id: h.worker_id,
      name: h.worker_name,
      role: h.role,
      avatarColor: 'from-neutral-200 to-neutral-300',
      initial: (h.worker_name || 'A').charAt(0).toUpperCase(),
    };
  }, [activeId, hires]);

  return (
    <div className="flex min-h-screen flex-col bg-white text-neutral-900">
      <Navbar onSignIn={() => {}} onSignUp={() => {}} />

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <aside className="flex w-72 shrink-0 flex-col border-r border-neutral-200 bg-neutral-50">
          <div className="flex items-center justify-between border-b border-neutral-200 px-4 py-3">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-neutral-500" />
              <h2 className="text-sm font-semibold text-neutral-800">Your workers</h2>
            </div>
            <span className="rounded-full bg-white px-2 py-0.5 text-[11px] font-medium text-neutral-600 ring-1 ring-inset ring-neutral-200">
              {hires.length}
            </span>
          </div>

          <div className="flex-1 overflow-y-auto p-2">
            {loading ? (
              <div className="flex justify-center py-8 text-neutral-400">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            ) : hires.length === 0 ? (
              <div className="px-3 py-8 text-center text-sm text-neutral-500">
                <p>You haven't hired anyone yet.</p>
                <Button
                  size="sm"
                  onClick={() => navigate('/')}
                  className="mt-4 bg-neutral-900 text-white hover:bg-neutral-800"
                >
                  <Plus className="mr-1 h-4 w-4" /> Browse workers
                </Button>
              </div>
            ) : (
              <ul className="space-y-1">
                {hires.map((h) => {
                  const w = getWorkerById(h.worker_id);
                  const gradient = w?.avatarColor || 'from-neutral-200 to-neutral-300';
                  const isActive = activeId === h.worker_id;
                  return (
                    <li key={h.id}>
                      <button
                        onClick={() => setActiveId(h.worker_id)}
                        className={
                          'flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left transition-colors ' +
                          (isActive ? 'bg-white ring-1 ring-neutral-200' : 'hover:bg-white/70')
                        }
                      >
                        <div className={`flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-gradient-to-br ${gradient}`}>
                          {w?.avatar ? (
                            <img src={w.avatar} alt={h.worker_name} className="h-full w-full object-cover" />
                          ) : (
                            <span className="text-sm font-semibold text-neutral-800">
                              {(h.worker_name || '?').charAt(0).toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-neutral-900">{h.worker_name}</p>
                          <p className="truncate text-xs text-neutral-500">{h.role}</p>
                        </div>
                        {h.is_custom && (
                          <span className="rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 ring-1 ring-inset ring-emerald-200">
                            Custom
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="border-t border-neutral-200 p-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/')}
              className="w-full border-neutral-300 bg-white"
            >
              <Plus className="mr-1 h-4 w-4" /> Hire another
            </Button>
          </div>
        </aside>

        {/* Chat area */}
        <main className="flex flex-1 flex-col">
          {activeWorker ? (
            <ChatPanel worker={activeWorker} />
          ) : (
            <div className="flex flex-1 flex-col items-center justify-center bg-neutral-50 text-center">
              <h2 className="font-serif text-3xl tracking-tight text-neutral-900">Your workspace is empty</h2>
              <p className="mt-2 text-neutral-600">Hire your first worker to start chatting.</p>
              <Button onClick={() => navigate('/')} className="mt-6 bg-neutral-900 text-white hover:bg-neutral-800">
                <Plus className="mr-1 h-4 w-4" /> Browse workers
              </Button>
            </div>
          )}
        </main>
      </div>

      <Toaster />
    </div>
  );
};

export default Workspace;
